import React, { useCallback, useEffect, useState } from 'react';
import { checkMercadoPagoStatus, disconnectMercadoPago } from '../services/api';
import { getApiBaseUrl } from '../config/apiConfig';
import { getCurrentToken } from '../utils/userStorage';
import { mapCategoryToEvent, mapEventCategoryToApi } from '../utils/peekPassBrand';
import MercadoPagoLinkSuccessOverlay from './MercadoPagoLinkSuccessOverlay';
import ProducerWorkspace from './ProducerWorkspace';

interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: 'client' | 'organizer' | 'admin';
  isVerified: boolean;
  isFirstLogin?: boolean;
  interests?: string[];
}

interface ClientDashboardProps {
  userData: UserData;
  onLogout: () => void;
  onGoHome?: () => void;
}

interface EventForm {
  title: string;
  description: string;
  eventForms: string;
  serviceFeeRate: string;
  category: string;
  difficulty: string;
  price: string;
  discount: string;
  classDuration: string;
  eventDate: string;
  ageRestriction: string;
  venueName: string;
  venueAddress: string;
  venueCity: string;
  ticketType: string;
  ticketCapacity: string;
  ticketTiers: TicketTier[];
  autoPublishAt: string;
  organizerOrigin: string;
  language: string;
  video: File | null;
  photo: File | null;
  benefits: string[];
  requirements: string[];
}

interface TicketTier {
  id: string;
  name: string;
  quantity: string;
  price: string;
  availableFrom: string;
  availableUntil: string;
}

const createTicketTiers = (): TicketTier[] => ([
  { id: 'general', name: 'General', quantity: '', price: '', availableFrom: '', availableUntil: '' },
  { id: 'early-bird', name: 'Early Bird', quantity: '', price: '', availableFrom: '', availableUntil: '' },
  { id: 'vip', name: 'VIP', quantity: '', price: '', availableFrom: '', availableUntil: '' },
  { id: 'second-release', name: 'Segunda tanda', quantity: '', price: '', availableFrom: '', availableUntil: '' }
]);

const allowedEventCoverTypes = ['image/jpeg', 'image/png', 'image/webp'];
const serviceFeePreferenceKey = 'peekpass-service-fee-rate';

const normalizeServiceFeeRate = (value: unknown) => {
  const rate = Number(value);
  return Number.isFinite(rate) ? Math.min(18, Math.max(5, Math.round(rate))) : 5;
};

const getPreferredServiceFeeRate = () => {
  if (typeof window === 'undefined') return '5';
  try {
    return String(normalizeServiceFeeRate(window.localStorage.getItem(serviceFeePreferenceKey)));
  } catch {
    return '5';
  }
};

const savePreferredServiceFeeRate = (value: unknown) => {
  const serviceFeeRate = String(normalizeServiceFeeRate(value));
  try {
    window.localStorage.setItem(serviceFeePreferenceKey, serviceFeeRate);
  } catch {
    // La selección sigue funcionando durante la sesión aunque el navegador bloquee localStorage.
  }
  return serviceFeeRate;
};

const emptyEventForm = (): EventForm => ({
  title: '',
  description: '',
  eventForms: '',
  serviceFeeRate: getPreferredServiceFeeRate(),
  category: 'Tendencias',
  difficulty: 'Principiante',
  price: '',
  discount: '',
  classDuration: '',
  eventDate: '',
  ageRestriction: '',
  venueName: '',
  venueAddress: '',
  venueCity: '',
  ticketType: 'General',
  ticketCapacity: '',
  ticketTiers: createTicketTiers(),
  autoPublishAt: '',
  organizerOrigin: '',
  language: 'Español',
  video: null,
  photo: null,
  benefits: [],
  requirements: []
});

const toDateTimeLocal = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
};

const ticketTiersFromEvent = (event: any): TicketTier[] => {
  if (Array.isArray(event.ticketTiers) && event.ticketTiers.length) {
    return event.ticketTiers.map((tier: any, index: number) => ({
      id: tier.id || `tier-${index + 1}`,
      name: tier.name || `Tanda ${index + 1}`,
      quantity: tier.quantity === undefined || tier.quantity === null ? '' : String(tier.quantity),
      price: tier.price === undefined || tier.price === null ? '' : String(tier.price),
      availableFrom: toDateTimeLocal(tier.availableFrom),
      availableUntil: toDateTimeLocal(tier.availableUntil)
    }));
  }

  const fallback = createTicketTiers();
  fallback[0] = {
    ...fallback[0],
    name: event.ticketType || 'General',
    quantity: event.ticketCapacity ? String(event.ticketCapacity) : '',
    price: event.price === undefined || event.price === null ? '' : String(event.price)
  };
  return fallback;
};

const ClientDashboard: React.FC<ClientDashboardProps> = ({ userData, onLogout, onGoHome }) => {
  const [mercadoPagoLinked, setMercadoPagoLinked] = useState(false);
  const [mercadoPagoLoading, setMercadoPagoLoading] = useState(false);
  const [showMercadoPagoSuccess, setShowMercadoPagoSuccess] = useState(false);
  const [myReels, setMyReels] = useState<any[]>([]);
  const [loadingReels, setLoadingReels] = useState(false);
  const [eventForm, setEventForm] = useState<EventForm>(emptyEventForm);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishProgress, setPublishProgress] = useState(0);

  const loadMercadoPagoStatus = useCallback(async () => {
    const response = await checkMercadoPagoStatus();
    setMercadoPagoLinked(response.isLinked);
    return response;
  }, []);

  const loadMyReels = useCallback(async () => {
    setLoadingReels(true);
    try {
      const token = getCurrentToken();
      if (!token) return;

      const response = await fetch(`${getApiBaseUrl()}/classes/shorts/my-reels`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);

      const result = await response.json();
      setMyReels(result.data || []);
    } catch (error) {
      console.error('Error cargando eventos publicados:', error);
    } finally {
      setLoadingReels(false);
    }
  }, []);

  useEffect(() => {
    loadMercadoPagoStatus().catch((error) => console.error('Error cargando Mercado Pago:', error));
    loadMyReels();
  }, [loadMercadoPagoStatus, loadMyReels]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mercadopago') === 'success') {
      window.history.replaceState({}, document.title, window.location.pathname);
      loadMercadoPagoStatus()
        .then(() => setShowMercadoPagoSuccess(true))
        .catch((error) => console.error('Error actualizando Mercado Pago:', error));
    }

    if (params.get('mercadopago') === 'error' || params.get('error')) {
      const details = params.get('details');
      window.history.replaceState({}, document.title, window.location.pathname);
      alert(details ? `No pudimos vincular Mercado Pago. ${details}` : 'No pudimos vincular Mercado Pago. Intentá nuevamente.');
    }
  }, [loadMercadoPagoStatus]);

  useEffect(() => {
    if (!showMercadoPagoSuccess) return;

    const hideSuccessOverlay = window.setTimeout(() => {
      setShowMercadoPagoSuccess(false);
    }, 3400);

    return () => window.clearTimeout(hideSuccessOverlay);
  }, [showMercadoPagoSuccess]);

  const handleConnectMercadoPago = async () => {
    try {
      setMercadoPagoLoading(true);
      const token = getCurrentToken();
      if (!token) throw new Error('No estás autenticado. Iniciá sesión nuevamente.');

      const response = await fetch(`${getApiBaseUrl()}/auth/mercadopago/connect`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al conectar con Mercado Pago');
      }

      const data = await response.json();
      if (!data.redirectUrl) throw new Error('No se recibió URL de redirección');
      window.location.href = data.redirectUrl;
    } catch (error: any) {
      console.error('Error vinculando Mercado Pago:', error);
      alert(`No pudimos vincular Mercado Pago: ${error.message}`);
      setMercadoPagoLoading(false);
    }
  };

  const handleDisconnectMercadoPago = async () => {
    if (!window.confirm('¿Querés desvincular tu cuenta de Mercado Pago? Los próximos eventos no podrán cobrar hasta volver a conectarla.')) return;
    try {
      await disconnectMercadoPago();
      await loadMercadoPagoStatus();
      alert('Cuenta de Mercado Pago desvinculada.');
    } catch (error: any) {
      console.error('Error desvinculando Mercado Pago:', error);
      alert(`No pudimos desvincular la cuenta: ${error.message}`);
    }
  };

  const handleEventFormChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    if (name === 'serviceFeeRate') {
      savePreferredServiceFeeRate(value);
    }
    setEventForm((current) => ({ ...current, [name]: value }));
  };

  const handleServiceFeeRateChange = (value: number) => {
    const serviceFeeRate = savePreferredServiceFeeRate(value);
    setEventForm((current) => ({ ...current, serviceFeeRate }));
  };

  const handleTicketTierChange = (tierId: string, field: keyof Omit<TicketTier, 'id'>, value: string) => {
    setEventForm((current) => ({
      ...current,
      ticketTiers: current.ticketTiers.map((tier) => tier.id === tierId ? { ...tier, [field]: value } : tier)
    }));
  };

  const handleAddTicketTier = () => {
    setEventForm((current) => ({
      ...current,
      ticketTiers: [...current.ticketTiers, {
        id: `tier-${Date.now()}`,
        name: 'Nueva tanda',
        quantity: '',
        price: '',
        availableFrom: '',
        availableUntil: ''
      }]
    }));
  };

  const handleRemoveTicketTier = (tierId: string) => {
    setEventForm((current) => ({
      ...current,
      ticketTiers: current.ticketTiers.filter((tier) => tier.id !== tierId)
    }));
  };

  const handleEventVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && !file.type.startsWith('video/')) {
      alert('Seleccioná un archivo de video válido.');
      return;
    }
    setEventForm((current) => ({ ...current, video: file }));
  };

  const handleEventPhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (file && !allowedEventCoverTypes.includes(file.type)) {
      alert('Seleccioná una portada JPG, PNG o WEBP.');
      return;
    }
    if (file && file.size > 10 * 1024 * 1024) {
      alert('La portada debe ser menor a 10MB.');
      return;
    }
    setEventForm((current) => ({ ...current, photo: file }));
  };

  const handlePublishEvent = async () => {
    const isEditing = Boolean(editingEventId);
    const configuredTicketTiers = eventForm.ticketTiers
      .map((tier) => ({ ...tier, name: tier.name.trim() }))
      .filter((tier) => tier.name || tier.quantity || tier.price || tier.availableFrom || tier.availableUntil);

    if (!configuredTicketTiers.length || configuredTicketTiers.some((tier) => !tier.name || !tier.quantity || Number(tier.quantity) <= 0 || tier.price === '' || Number(tier.price) < 0)) {
      alert('Configurá al menos una tanda de tickets con nombre, cupo y precio válidos.');
      return;
    }

    if (configuredTicketTiers.some((tier) => tier.availableFrom && tier.availableUntil && new Date(tier.availableUntil) <= new Date(tier.availableFrom))) {
      alert('La fecha de cierre de una tanda debe ser posterior a su fecha de habilitación.');
      return;
    }

    const totalTicketCapacity = configuredTicketTiers.reduce((total, tier) => total + Number(tier.quantity), 0);
    const lowestTicketPrice = Math.min(...configuredTicketTiers.map((tier) => Number(tier.price)));

    if (!eventForm.title || !eventForm.description || !eventForm.eventDate || !eventForm.venueName || !eventForm.venueAddress || !eventForm.venueCity || (!isEditing && !eventForm.photo)) {
      alert(isEditing
        ? 'Completá nombre, descripción, fecha y Location para guardar los cambios.'
        : 'Completá nombre, descripción, fecha, Location y portada para publicar.');
      return;
    }

    const discount = eventForm.discount === '' ? 0 : Number(eventForm.discount);
    if (Number.isNaN(discount) || discount < 0 || discount > 100) {
      alert('El descuento debe ser un valor entre 0 y 100.');
      return;
    }
    const serviceFeeRate = eventForm.serviceFeeRate === '' ? 5 : Number(eventForm.serviceFeeRate);
    if (Number.isNaN(serviceFeeRate) || serviceFeeRate < 5 || serviceFeeRate > 18) {
      alert('El cargo por servicio debe estar entre 5% y 18%.');
      return;
    }
    if (eventForm.video && eventForm.video.size > 100 * 1024 * 1024) {
      alert('El teaser no puede superar 100MB.');
      return;
    }
    if (!isEditing && !mercadoPagoLinked) {
      if (window.confirm('Para publicar y vender necesitás vincular Mercado Pago. ¿Querés conectarlo ahora?')) {
        await handleConnectMercadoPago();
      }
      return;
    }

    const token = getCurrentToken();
    if (!token) {
      alert('No estás autenticado. Iniciá sesión nuevamente.');
      return;
    }

    setIsPublishing(true);
    setPublishProgress(0);
    let interval: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      interval = setInterval(() => setPublishProgress((progress) => Math.min(progress + 12, 85)), 450);
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), 120000);
      const formData = new FormData();
      formData.append('title', eventForm.title);
      formData.append('description', eventForm.description);
      formData.append('eventForms', eventForm.eventForms);
      formData.append('serviceFeeRate', String(serviceFeeRate));
      formData.append('category', mapEventCategoryToApi(eventForm.category));
      formData.append('difficulty', eventForm.difficulty);
      formData.append('price', String(lowestTicketPrice));
      formData.append('discount', String(discount));
      formData.append('classDuration', eventForm.classDuration ? `${eventForm.classDuration} min` : '');
      formData.append('eventDate', eventForm.eventDate);
      formData.append('ageRestriction', eventForm.ageRestriction);
      formData.append('venueName', eventForm.venueName);
      formData.append('venueAddress', eventForm.venueAddress);
      formData.append('venueCity', eventForm.venueCity);
      formData.append('ticketType', configuredTicketTiers[0].name);
      formData.append('ticketCapacity', String(totalTicketCapacity));
      formData.append('ticketTiers', JSON.stringify(configuredTicketTiers));
      formData.append('autoPublishAt', eventForm.autoPublishAt);
      formData.append('organizerOrigin', eventForm.organizerOrigin);
      formData.append('language', eventForm.language);
      if (eventForm.video) formData.append('video', eventForm.video);
      if (eventForm.photo) formData.append('photo', eventForm.photo);
      formData.append('benefits', JSON.stringify(eventForm.benefits));
      formData.append('requirements', JSON.stringify(eventForm.requirements));

      const response = await fetch(
        `${getApiBaseUrl()}/classes/shorts${isEditing ? `/${editingEventId}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
          signal: controller.signal
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: any = {};
        try { errorData = JSON.parse(errorText); } catch { /* The API may return plain text errors. */ }
        if (errorData.requiresMercadoPago && window.confirm('Mercado Pago debe estar vinculado para continuar. ¿Querés conectarlo ahora?')) {
          await handleConnectMercadoPago();
          return;
        }
        throw new Error(errorData.error || `Error del servidor: ${response.status}`);
      }

      const result = await response.json();
      if (!result.success) throw new Error(result.error || 'No pudimos publicar el evento.');

      setPublishProgress(100);
      setEventForm(emptyEventForm());
      setEditingEventId(null);
      await loadMyReels();
      alert(isEditing ? 'Cambios guardados correctamente.' : 'Evento publicado correctamente.');
    } catch (error: any) {
      console.error('Error publicando evento:', error);
      const action = isEditing ? 'guardar los cambios' : 'publicar el evento';
      alert(error.name === 'AbortError' ? 'La operación tardó demasiado. Intentá nuevamente.' : `No pudimos ${action}: ${error.message}`);
    } finally {
      if (interval) clearInterval(interval);
      if (timeoutId) clearTimeout(timeoutId);
      setIsPublishing(false);
      setPublishProgress(0);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!eventId || eventId.startsWith('workspace-')) return;
    if (!window.confirm('¿Querés eliminar este evento publicado?')) return;
    try {
      const token = getCurrentToken();
      if (!token) throw new Error('Token no encontrado');
      const response = await fetch(`${getApiBaseUrl()}/classes/shorts/${eventId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`Error: ${response.status} ${response.statusText}`);
      if (editingEventId === eventId) {
        setEditingEventId(null);
        setEventForm(emptyEventForm());
      }
      await loadMyReels();
      alert('Evento eliminado.');
    } catch (error: any) {
      console.error('Error eliminando evento:', error);
      alert(`No pudimos eliminar el evento: ${error.message}`);
    }
  };

  const handleEditEvent = (event: any) => {
    setEditingEventId(event._id || null);
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      eventForms: event.eventForms || '',
      serviceFeeRate: event.serviceFeeRate === undefined || event.serviceFeeRate === null ? '5' : String(event.serviceFeeRate),
      category: mapCategoryToEvent(event.category),
      difficulty: event.difficulty || 'Principiante',
      price: event.price === undefined || event.price === null ? '' : String(event.price),
      discount: event.discount === undefined || event.discount === null ? '' : String(event.discount),
      classDuration: String(event.duration || '').replace(/\D/g, ''),
      eventDate: toDateTimeLocal(event.eventDate),
      ageRestriction: event.ageRestriction || '',
      venueName: event.venue?.name || '',
      venueAddress: event.venue?.address || '',
      venueCity: event.venue?.city || event.organizer?.country || '',
      ticketType: event.ticketType || 'General',
      ticketCapacity: event.ticketCapacity ? String(event.ticketCapacity) : '',
      ticketTiers: ticketTiersFromEvent(event),
      autoPublishAt: toDateTimeLocal(event.autoPublishAt),
      organizerOrigin: event.organizerOrigin || event.organizer?.country || '',
      language: event.language || 'Español',
      video: null,
      photo: null,
      benefits: Array.isArray(event.benefits) ? event.benefits : [],
      requirements: Array.isArray(event.requirements) ? event.requirements : []
    });
  };

  return (
    <>
      <ProducerWorkspace
        userData={userData}
        onLogout={onLogout}
        onGoHome={onGoHome}
        mercadoPagoLinked={mercadoPagoLinked}
        mercadoPagoLoading={mercadoPagoLoading}
        onConnectMercadoPago={handleConnectMercadoPago}
        onDisconnectMercadoPago={handleDisconnectMercadoPago}
        events={myReels}
        eventsLoading={loadingReels}
        onDeleteEvent={handleDeleteEvent}
        onEditEvent={handleEditEvent}
        eventForm={eventForm}
        onEventFormChange={handleEventFormChange}
        onServiceFeeRateChange={handleServiceFeeRateChange}
        onTicketTierChange={handleTicketTierChange}
        onAddTicketTier={handleAddTicketTier}
        onRemoveTicketTier={handleRemoveTicketTier}
        onEventVideoChange={handleEventVideoChange}
        onEventPhotoChange={handleEventPhotoChange}
        onPublishEvent={handlePublishEvent}
        isEditingEvent={Boolean(editingEventId)}
        isPublishing={isPublishing}
        publishProgress={publishProgress}
      />
      <MercadoPagoLinkSuccessOverlay visible={showMercadoPagoSuccess} />
    </>
  );
};

export default ClientDashboard;
